import React, { FunctionComponent, useEffect, useState, useRef, ReactNode, } from 'react';
import {
  Button,
  Col,
  Dropdown,
  DropdownButton,
  FormControl,
  Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
  HeaderDisplayKeysWithType,
  TextField,
  OverflowWithTooltip,
  InlineEditableTable,
  InputDropdownGroupField
} from '../../../components';
import { useClassGrade, useSemGrade } from '../../../hook';
import { StudentGradeNoSeat, PHISightSolid, UserInfo } from '../../../model';
import apis from '../../../apis';
import { SightContext } from '../../TableHeaders/SightHeader';
import {
  downloadDataAsExcel,
  downloadDataAsPDFByPrint,
  SheetHeaderDisplay,
} from '../../../utils';
import { Nullable } from '../../../types';
import { toast } from 'react-toastify';
import { GradeSem, WrapGradeBaseLayout } from '../../../layouts';
import { I18N } from '../../../i18n-raw';

const sightSolidKind = [
  {
    Id: 0,
    Name: '斜視',
  },
  {
    Id: 1,
    Name: '弱視',
  },
  {
    Id: 2,
    Name: '斜、弱視',
  },
  {
    Id: 3,
    Name: '屈光不正',
  },
  {
    Id: 4,
    Name: '無確定診斷',
  },
  {
    Id: 5,
    Name: '診治正常',
  },
  {
    Id: 6,
    Name: '其他眼疾',
  },
  {
    Id: 7,
    Name: '未就醫',
  }
];
const sightSolidOptions: Record<number, string> = {};
sightSolidKind.forEach(k => {
  sightSolidOptions[k.Id] = k.Name;
});
const transformTextInput = (
  property: string,
  v: unknown,
  i: number,
  e?: boolean
) => (e ? <TextField property={property} /> : wrapOverflow(v as string));
function wrapOverflow(content: ReactNode) {
  return <OverflowWithTooltip>{content}</OverflowWithTooltip>;
}
type PageData = StudentGradeNoSeat & Nullable<PHISightSolid>;

const tableHeader: (SheetHeaderDisplay<PageData> &
  HeaderDisplayKeysWithType<PageData>)[] = [
    { display: '年級', property: 'grade' },
    {
      display: '班級',
      property: 'className',
      onRender: (value: unknown) => {
        const num = parseInt(value as string, 10);
        return !isNaN(num) && num > 100 && num < 200 ? num - 100 : value;
      },
      onSheetRender: (value) => {
        const num = parseInt(value as string, 10);
        if (!isNaN(num)) {
          return num > 100 && num < 200 ? num - 100 : num;
        }
        return value?.toString() ?? '';
      },
    },
    { display: '學生', property: 'name' },
    { display: '性別', property: 'sex' },
    { display: '座號', property: 'seat' },
    { display: '統編', property: 'pid' },
    {
      property: 'sightSolid',
      display: '診斷',
      onRender: (value: unknown, _i: number, editing?: boolean) => {
        if (editing) {
          return (
            <InputDropdownGroupField
              content={sightSolidOptions}
              property="sightSolid"
              selectShow="value"
              resultShow="value"
              type="number"
              needGroup={false}
              resultClassName="wid-160"
            />
          );
        }
        const idx = typeof value === 'number' ? value : parseInt(String(value), 10);
        return Number.isFinite(idx) && sightSolidKind[idx]
          ? sightSolidKind[idx].Name
          : '無診斷';
      },
    },
    {
      property: 'states',
      display: '其他診斷',
      onRender: transformTextInput.bind(null, 'states'),
    },
    {
      property: 'hospital',
      display: '醫院',
      onRender: transformTextInput.bind(null, 'hospital'),
    }
  ];

const mapState = (state: ApplicationState, ownProps: GradeSem) => ({
  ...ownProps,
});

const mapDispatch = { ...ErrorDispatches };

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const sightSolid: FunctionComponent<Props> = ({
  grade,
  gradeName,
  year,
  sem,
  catchErrorForModal,
}) => {
  const { yearSem, element: semGradeElement } = useSemGrade();
  const { selectedClassGrade: classGrade, element: classGradeElement } =
    useClassGrade({
      yearSem,
    });
  const [students, setStudents] = useState<PageData[]>([]);
  const studentInputRef = useRef<HTMLInputElement>(null);
  const [insertBtn, setInsertBtn] = useState<boolean>(true);
  const [studentSid, setStudentSid] = useState<string>('');
  const [studentPid, setStudentPid] = useState<string>('');
  const [studentGrade, setStudentGrade] = useState<number>();
  const [no, setNo] = useState<number>();
  const [seat, setSeat] = useState<number>();
  const [checkField, setCheckField] = useState<number>(0);
  const [userinfo, setUser] = useState<UserInfo>();
  useEffect(() => {
    apis.getUserInfo()
      .then((u) => {
        setUser(u);
      })
      .catch(catchErrorForModal);

    getStudentSightSolid();
  }, [grade]);

  function getStudentSightSolid() {
    if (grade) {
      toast
        .promise(
          apis
            .getStudentSightSolidListByGrade(grade, year, sem)
            .then(r => {
              setStudents(
                r.map(({ sightSolid, ...s }) => ({
                  ...sightSolid,
                  ...s,
                }))
              )
            }),
          {
            pending: '資料載入中......',
            success: '資料載入成功！',
            error: '資料載入失敗！請查看錯誤資訊。',
          }
        )
        .catch(catchErrorForModal);
    }
  }
  // 檢查是否可以點選新增按鈕
  function CheckInsertBtn() {
    // 若尚未選擇年級，保持按鈕停用
    if (!grade) {
      setInsertBtn(true);
      return;
    }

    setInsertBtn(true);
    if (studentInputRef.current?.value) {
      setInsertBtn(false);
      CheckInput();
    }
  }
  function CheckInput() {
    const inputStudent = studentInputRef.current?.value;

    // 檢查輸入身分證、學號或班級座號
    if (inputStudent) {
      if (inputStudent.substring(0, 1) == '=') {
        setStudentSid(inputStudent.substring(1));
        setCheckField(2);
      } else {
        switch (inputStudent.length) {
          case 5:
            setStudentGrade(parseInt(inputStudent.substring(0, 1)));
            setNo(parseInt(inputStudent.substring(1, 3)));
            setSeat(parseInt(inputStudent.substring(3, 5)));
            setCheckField(3);
            break;
          case 6:
            // 代入年級、班級、座號
            setStudentGrade(parseInt(inputStudent.substring(0, 2)));
            setNo(parseInt(inputStudent.substring(2, 4)));
            setSeat(parseInt(inputStudent.substring(2, 4)));
            setCheckField(3);
            break;
          case 10:
          case 11:
          case 12:
            setStudentPid(inputStudent);
            setCheckField(1);
            break;
        }
      }
    }
  }

  function insertSightSolidCheck() {
    // 檢查診斷代號
    // 檢查有無年級班級座號
    if (checkField == 3) {
      if (!studentGrade || !no || !seat) {
        toast.error(`找不到該名學生!`);
        return
      }
    }

    insertSightSolid();
  }

  function insertSightSolid() {
    toast
      .promise(
        apis.insertNoticeSightSolid(
          year,
          sem,
          studentSid,
          seat || 0,
          no || 0,
          grade || 0,
          studentPid,
          -1,
          "",
          "",
          checkField
        ),
        {
          pending: '資料新增中......',
          success: '新增成功！',
        }
      )
      .then((r) => {
        // 清空學號
        if (studentInputRef.current) {
          studentInputRef.current.value = '';
        }
        CheckInsertBtn(); // 按鈕disabled
        getStudentSightSolid();
      })
      .catch(catchErrorForModal);
  }

  function onValueUpdate(value: PageData) {
    if (value.id) {
      apis
        .updateStudentSightSolid(
          value.id,
          value?.sightSolid || 0,
          value?.states || "",
          value?.hospital || "",
          value.pid,
        )
        .then(() => {
          getStudentSightSolid();
        })
        .catch(catchErrorForModal);
    }
  }

  function onValueDelete(value: PageData) {
    if (value.id) {
      apis
        .deleteStudentSightSolid(
          value.id,
          value?.sightSolid || 0,
          value?.states || "",
          value?.hospital || "",
          value.pid,
        )
        .then(() => {
          getStudentSightSolid();
        })
        .catch(catchErrorForModal);
    }
  }

  const title = `${year}學年${sem}學期${grade}年級_立體感異常清單`;

  const handleExcel = () => {
    downloadDataAsExcel({
      title,
      values: students,
      headers: tableHeader,
      context: SightContext,
      footer: '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
      exportfile: userinfo?.exportFileType,
    });
  };

  const handlePrint = () => {
    downloadDataAsPDFByPrint({
      title,
      values: students,
      headers: tableHeader,
      context: SightContext,
      footer: '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
    });
  };

  const handleNoticePrint = () => {
    if (!students.length) return;

    const pids = students.map((s) => s.pid);

    // 先開空白視窗，避免 API 完成後才 window.open 被瀏覽器擋掉
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      toast.error('瀏覽器阻擋了列印視窗，請允許彈出式視窗。');
      return;
    }

    toast
      .promise(
        apis.downloadSolidPDF(
          grade || 0,
          pids,
          year,
          sem
        ),
        {
          pending: '通知單產生中...',
          success: '通知單產生成功！',
          error: '通知單產生失敗！請查看錯誤資訊。',
        }
      )
      .then((blob) => {
        const pdfBlob = new Blob(
          [blob],
          { type: 'application/pdf' }
        );

        const pdfUrl =
          window.URL.createObjectURL(pdfBlob);

        // 直接在剛剛開啟的視窗顯示 PDF
        printWindow.location.href = pdfUrl;

        // 不使用 <a download>
        // 所以不會先下載 PDF
      })
      .catch((error) => {
        printWindow.close();
        catchErrorForModal(error);
      });
  };

  return (
    <>
      <Row className="justify-content-between">
        <Col className="text-right">
          <div className="d-flex justify-content-end">

            <DropdownButton
              id="sight-solid-export"
              title="匯出"
              variant="success"
              disabled={!students.length}
              className="mr-3"
            >
              <Dropdown.Item onClick={handleExcel}>
                Excel 下載
              </Dropdown.Item>

              <Dropdown.Item onClick={handlePrint}>
                列印
              </Dropdown.Item>
            </DropdownButton>

            <Button
              disabled={!students.length}
              onClick={handleNoticePrint}
            >
              通知單列印
            </Button>

          </div>
        </Col>
      </Row>
      <Row>
        <div>
          <span>請輸入年級班級座號或身分證或學號(學號前請加=)：</span><br />
          <span className="text-primary">例如:國小三年級1班3號，應寫作「30103」</span>
        </div>
        <Col xs={2} className="mr-2">
          <FormControl
            type="string"
            aria-label="studentInput"
            aria-describedby="basic-addon1"
            ref={studentInputRef}
            onChange={CheckInsertBtn}
          />
        </Col>
        <Button
          disabled={insertBtn || !grade}
          onClick={insertSightSolidCheck}
        >
          {grade ? '新增異常學生' : '請先選擇年級'}
        </Button>
      </Row>
      <hr />
      <Row>
        <InlineEditableTable
          values={students}
          headers={tableHeader}
          onValueUpdate={onValueUpdate}
          onValueDelete={onValueDelete}
          onRowRender={(v) => ({
            editable: true,
            deleteable: true
          })}
        />
      </Row>
    </>
  );
};

export const SightSolid = connector(WrapGradeBaseLayout(sightSolid));
